import type { TrialRecap } from '@/hooks/useTrialRecap';
import { Button } from '@/components/ui/button';
import { Sparkles } from 'lucide-react';

interface Props {
  recap: TrialRecap | null;
  onContinue: () => void;
}

const STAGE_LABEL: Record<string, string> = {
  egg: 'Egg',
  baby: 'Baby',
  common: 'Common',
  rare: 'Rare',
  epic: 'Epic',
  legendary: 'Legendary',
};

export function PageFennieAI({ recap, onContinue }: Props) {
  const stage = recap?.fennie.stage ?? 'baby';
  const health = recap?.fennie.healthScore ?? 0;
  const insights = recap?.fennie.aiInsights ?? 0;
  const actions = recap?.fennie.aiActions ?? 0;

  return (
    <div className="min-h-full flex flex-col px-6 py-12 max-w-lg mx-auto">
      <p className="text-[10px] uppercase tracking-[0.3em] text-primary/70 mb-3">
        Chapter four
      </p>
      <h1 className="text-3xl sm:text-4xl font-display font-semibold tracking-tight mb-6">
        Fennie grew<br />alongside me.
      </h1>

      <div className="rounded-2xl border border-primary/25 bg-gradient-to-br from-primary/10 via-transparent to-transparent p-6 mb-4">
        <p className="text-xs text-muted-foreground/70 uppercase tracking-widest mb-1">
          Current stage
        </p>
        <p className="text-3xl font-bold tracking-tight">
          {STAGE_LABEL[stage] ?? stage}
        </p>
        <div className="mt-4 flex items-center gap-2">
          <div className="flex-1 h-2 rounded-full bg-muted/20 overflow-hidden">
            <div
              className="h-full bg-primary"
              style={{ width: `${Math.max(0, Math.min(100, health))}%` }}
            />
          </div>
          <span className="text-xs text-muted-foreground/70 tabular-nums">
            {health}/100
          </span>
        </div>
        <p className="text-[11px] text-muted-foreground/60 mt-1">
          health score
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-6">
        <div className="rounded-2xl border border-border/30 bg-muted/5 p-5">
          <div className="flex items-center gap-2 mb-2 text-primary/80">
            <Sparkles className="h-4 w-4" />
            <span className="text-[10px] uppercase tracking-widest">
              Insights
            </span>
          </div>
          <p className="text-2xl font-bold tracking-tight">{insights}</p>
          <p className="text-[11px] text-muted-foreground/60 mt-1">
            AI insights read
          </p>
        </div>
        <div className="rounded-2xl border border-border/30 bg-muted/5 p-5">
          <p className="text-[10px] uppercase tracking-widest text-primary/80 mb-2">
            AI actions
          </p>
          <p className="text-2xl font-bold tracking-tight">{actions}</p>
          <p className="text-[11px] text-muted-foreground/60 mt-1">
            categorised, parsed, explained
          </p>
        </div>
      </div>

      <p className="text-xs text-muted-foreground/60 leading-relaxed mb-8">
        On free, Fennie stops evolving and AI insights pause.
      </p>

      <div className="mt-auto pt-6">
        <Button onClick={onContinue} className="w-full" size="lg">
          See what I keep with Premium
        </Button>
      </div>
    </div>
  );
}
